/**
 * Trace GraphQL Fragments
 *
 * Reusable fragments for Trace primitive.
 * OpenTelemetry W3C Trace Context compliance.
 */

import { gql } from '@apollo/client';

/**
 * Complete Trace fields fragment
 *
 * Fields with #[graphql(skip)] in backend are excluded:
 *   tenantId, workflowInstanceId, actorId, taskId, triggerEntityId, createdBy
 * These are resolved via actor summary resolvers or are internal refs.
 */
export const TRACE_FIELDS = gql`
  fragment TraceFields on Trace {
    # Identity
    id
    version

    # Distributed Tracing
    traceId
    spanId
    parentSpanId
    traceContext
    baggage
    spanType
    operationName

    # Temporal
    startedAt
    endedAt
    durationMs
    timestamp

    # Actor/Entity Context (exposed fields only)
    triggerEntityType
    serviceName

    # Decision Telemetry
    decisionConfidence
    decisionReasoning
    evidenceSources
    alternativeOptions
    policyEvaluationResults
    validationResults

    # Performance Metrics
    tokensUsed
    memoryMb
    cpuPercent
    networkBytes
    cacheHits
    cacheMisses

    # Status/Error
    status
    errorCode
    errorMessage
    errorStackTrace

    # Audit Trail
    createdAt
    metadata
    tags
    attributes

    # Append-Only Architecture
    parentId
    originalId
    isActive

    # Complex Object resolvers
    compressedId
    actor {
      id
      displayName
    }
    createdByActor {
      id
      displayName
    }
  }
`;

/**
 * Trace connection fields
 */
export const TRACE_CONNECTION_FIELDS = gql`
  ${TRACE_FIELDS}
  fragment TraceConnectionFields on TraceConnection {
    items {
      ...TraceFields
    }
    nextCursor
    hasMore
  }
`;

/**
 * Performance statistics fields
 */
export const PERFORMANCE_STATS_FIELDS = gql`
  fragment PerformanceStatsFields on PerformanceStats {
    workflowInstanceId
    totalSpans
    p50DurationMs
    p95DurationMs
    p99DurationMs
    avgDurationMs
    successCount
    failureCount
    totalCacheHits
    totalCacheMisses
    cacheHitRate
  }
`;

/**
 * AI decision statistics fields
 */
export const AI_DECISION_STATS_FIELDS = gql`
  fragment AIDecisionStatsFields on AIDecisionStats {
    workflowInstanceId
    totalDecisions
    highConfidenceCount
    mediumConfidenceCount
    lowConfidenceCount
    avgConfidence
    totalTokensUsed
    avgTokensPerDecision
  }
`;
